import React, { useState, Component } from 'react';
import { Sparkle, Check, CaretDown, HouseLine, User, Star, X, Warning } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import type { StoredOffer, SavedProperty } from '../../lib/utils';
export interface UnconfirmedSuccessEntry {
  offer: StoredOffer;
  property: SavedProperty | null;
}
interface RentalConfirmationCalloutProps {
  entries: UnconfirmedSuccessEntry[];
  recentlyConfirmedEntries?: UnconfirmedSuccessEntry[];
  onConfirm: (entry: UnconfirmedSuccessEntry) => void;
  onReport: (entry: UnconfirmedSuccessEntry) => void;
  onViewTenantProfile?: (tenantSlug: string) => void;
  isAgent?: boolean;
  onRequestReview?: (entry: UnconfirmedSuccessEntry) => void;
  onDismissRecent?: (offerId: string) => void;
}
/**
 * Persistent success Callout for supply when one or more tenants have
 * accepted offers but the landlord/agent hasn't confirmed the rental yet.
 *
 * Renders three modes:
 *  - 1 entry         → full Callout with tenant, property, Confirmă, report link.
 *  - 2–4 entries     → stack of compact rows, each its own Confirmă, expandable.
 *  - 5+ entries      → condensed banner that expands inline to the stack.
 *
 * There is no dismiss action — the Callout stays until each entry is either
 * confirmed (markPropertyAsRented) or reported to support.
 *
 * Agents additionally see a post-confirmation banner to request a review.
 */
export function RentalConfirmationCallout({
  entries,
  recentlyConfirmedEntries = [],
  onConfirm,
  onReport,
  onViewTenantProfile,
  isAgent = false,
  onRequestReview,
  onDismissRecent
}: RentalConfirmationCalloutProps) {
  const [stackOpen, setStackOpen] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const hasEntries = entries.length > 0;
  const hasRecent = recentlyConfirmedEntries.length > 0;
  if (!hasEntries && !hasRecent) return null;
  const count = entries.length;
  const mode: 'single' | 'stack' | 'condensed' = count === 1 ? 'single' : count <= 4 ? 'stack' : 'condensed';
  const toggleRow = (id: string) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const getPropertyLabel = (entry: UnconfirmedSuccessEntry): string => {
    if (entry.property) return entry.property.title || (entry.property as any).name || 'Proprietate fără nume';
    return entry.offer.listingPreview?.title || 'Proprietate';
  };
  const renderPreConfirm = () => {
    if (!hasEntries) return null;
    // ── Single full Callout ────────────────────────────────────────────────
    if (mode === 'single') {
      const entry = entries[0];
      const tenantSlug = entry.offer.toSlug;
      const tenantName = entry.offer.toName;
      const propertyLabel = getPropertyLabel(entry);
      return <div className="rounded-2xl bg-lime-5 border border-lime-4 p-5" data-id="element-874">
          <div className="flex items-start gap-3" data-id="element-875">
            <div className="w-10 h-10 rounded-2xl bg-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-876">
              <Sparkle size={20} weight="fill" className="text-lime-1" data-id="element-877" />
            </div>
            <div className="min-w-0 flex-1" data-id="element-878">
              <h3 className="text-sm font-semibold text-light-1" data-id="element-879">
                🎉 Ai închiriat cu succes!
              </h3>
              <p className="text-sm text-light-2 mt-1 leading-relaxed" data-id="element-880">
                {tenantSlug && onViewTenantProfile ? <button type="button" onClick={() => onViewTenantProfile(tenantSlug)} className="font-medium text-lime-1 hover:text-lime-2 underline-offset-2 hover:underline cursor-pointer" data-id="element-881">
                    {tenantName}
                  </button> : <span className="font-medium text-light-1" data-id="element-882">{tenantName}</span>}{' '}
                a acceptat oferta ta pentru{' '}
                <span className="font-medium text-light-1" data-id="element-883">
                  {propertyLabel}
                </span>
                .
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-4" data-id="element-884">
                <button type="button" onClick={() => onConfirm(entry)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-xs font-semibold transition-colors cursor-pointer" data-id="element-885">
                  <Check size={14} weight="bold" data-id="element-886" />
                  Confirmă închirierea
                </button>
                <button type="button" onClick={() => onReport(entry)} className="inline-flex items-center gap-1.5 text-sm text-light-3 hover:text-light-1 hover:underline underline-offset-4 cursor-pointer transition-colors" data-id="element-887">
                  <Warning size={14} weight="regular" data-id="element-888" />
                  Ceva nu e în regulă
                </button>
              </div>
            </div>
          </div>
        </div>;
    }
    // ── Stack (2–4) ────────────────────────────────────────────────────────
    if (mode === 'stack') {
      return <div className="rounded-2xl bg-lime-5 border border-lime-4 overflow-hidden" data-id="element-889">
          <header className="flex items-center gap-3 px-5 py-3 border-b border-lime-4/60" data-id="element-890">
            <div className="w-8 h-8 rounded-2xl bg-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-891">
              <Sparkle size={16} weight="fill" className="text-lime-1" data-id="element-892" />
            </div>
            <div data-id="element-893">
              <h3 className="text-sm font-semibold text-light-1" data-id="element-894">
                🎉 {count} închirieri în așteptarea confirmării
              </h3>
              <p className="text-xs text-light-3 mt-0.5" data-id="element-895">
                Confirmă fiecare pentru a închide ofertele rămase pentru acea
                proprietate.
              </p>
            </div>
          </header>
          <ul className="divide-y divide-lime-4/40" data-id="element-896">
            {entries.map(entry => <RentalStackRow key={entry.offer.id} entry={entry} expanded={expandedRowIds.has(entry.offer.id)} onToggle={() => toggleRow(entry.offer.id)} onConfirm={() => onConfirm(entry)} onReport={() => onReport(entry)} onViewTenantProfile={onViewTenantProfile} propertyLabel={getPropertyLabel(entry)} data-id="element-897" />)}
          </ul>
        </div>;
    }
    // ── Condensed (5+) ─────────────────────────────────────────────────────
    return <div className="rounded-2xl bg-lime-5 border border-lime-4 overflow-hidden" data-id="element-898">
        <button type="button" onClick={() => setStackOpen(v => !v)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-lime-4/30 transition-colors cursor-pointer" aria-expanded={stackOpen} data-id="element-899">
          <div className="w-10 h-10 rounded-2xl bg-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-900">
            <Sparkle size={20} weight="fill" className="text-lime-1" data-id="element-901" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-902">
            <h3 className="text-sm font-semibold text-light-1" data-id="element-903">
              🎉 {count} chiriași au acceptat ofertele tale
            </h3>
            <p className="text-xs text-light-3 mt-0.5" data-id="element-904">
              Confirmă fiecare închiriere pentru a închide ofertele rămase.
            </p>
          </div>
          <CaretDown size={16} weight="bold" className={cn('text-light-2 shrink-0 transition-transform', stackOpen && 'rotate-180')} data-id="element-905" />
        </button>
        {stackOpen && <ul className="divide-y divide-lime-4/40 border-t border-lime-4/60" data-id="element-906">
            {entries.map(entry => <RentalStackRow key={entry.offer.id} entry={entry} expanded={expandedRowIds.has(entry.offer.id)} onToggle={() => toggleRow(entry.offer.id)} onConfirm={() => onConfirm(entry)} onReport={() => onReport(entry)} onViewTenantProfile={onViewTenantProfile} propertyLabel={getPropertyLabel(entry)} data-id="element-907" />)}
          </ul>}
      </div>;
  };
  const renderPostConfirm = () => {
    if (!isAgent || !hasRecent || !onRequestReview) return null;
    return <div className="space-y-3" data-id="element-908">
        {recentlyConfirmedEntries.map(entry => {
        const tenantName = entry.offer.toName;
        const propertyLabel = getPropertyLabel(entry);
        return <div key={`recent-${entry.offer.id}`} className="rounded-2xl bg-dark-2 border border-lime-4/40 p-4 flex items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-909">
              <div className="flex items-center gap-3 min-w-0" data-id="element-910">
                <div className="w-8 h-8 rounded-full bg-lime-5/20 flex items-center justify-center shrink-0" data-id="element-911">
                  <Check size={16} weight="bold" className="text-lime-1" data-id="element-912" />
                </div>
                <p className="text-sm text-light-2 truncate" data-id="element-913">
                  Închiriere confirmată cu{' '}
                  <span className="font-medium text-light-1" data-id="element-914">{tenantName}</span>{' '}
                  pentru{' '}
                  <span className="font-medium text-light-1" data-id="element-915">
                    {propertyLabel}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0" data-id="element-916">
                <button type="button" onClick={() => {
              onRequestReview(entry);
              onDismissRecent?.(entry.offer.id);
            }} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium border border-dark-4 transition-colors cursor-pointer" data-id="element-917">
                  <Star size={12} weight="fill" className="text-lime-1" data-id="element-918" />
                  Cere o recenzie
                </button>
                <button type="button" onClick={() => onDismissRecent?.(entry.offer.id)} className="w-8 h-8 rounded-md flex items-center justify-center text-light-4 hover:text-light-2 hover:bg-dark-3 transition-colors cursor-pointer" aria-label="Închide" data-id="element-919">
                  <X size={16} weight="bold" data-id="element-920" />
                </button>
              </div>
            </div>;
      })}
      </div>;
  };
  return <div className="space-y-4" data-id="element-921">
      {renderPreConfirm()}
      {renderPostConfirm()}
    </div>;
}
interface RentalStackRowProps {
  entry: UnconfirmedSuccessEntry;
  expanded: boolean;
  onToggle: () => void;
  onConfirm: () => void;
  onReport: () => void;
  onViewTenantProfile?: (slug: string) => void;
  propertyLabel: string;
}
function RentalStackRow({
  entry,
  expanded,
  onToggle,
  onConfirm,
  onReport,
  onViewTenantProfile,
  propertyLabel
}: RentalStackRowProps) {
  const tenantSlug = entry.offer.toSlug;
  const tenantName = entry.offer.toName;
  return <li data-id="element-922">
      <div className="px-5 py-3 flex items-center gap-3 flex-wrap" data-id="element-923">
        {entry.offer.fromAvatar ? <img src={`https://i.pravatar.cc/64?u=${encodeURIComponent(tenantSlug)}`} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" data-id="element-924" /> : <div className="w-9 h-9 rounded-full bg-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-925">
            <User size={16} weight="regular" className="text-lime-1" data-id="element-926" />
          </div>}
        <div className="min-w-0 flex-1" data-id="element-927">
          <div className="flex items-center gap-1.5 flex-wrap text-sm text-light-2" data-id="element-928">
            {tenantSlug && onViewTenantProfile ? <button type="button" onClick={() => onViewTenantProfile(tenantSlug)} className="font-medium text-light-1 hover:text-lime-1 transition-colors cursor-pointer" data-id="element-929">
                {tenantName}
              </button> : <span className="font-medium text-light-1" data-id="element-930">{tenantName}</span>}
            <span className="text-light-3" data-id="element-931">—</span>
            <span className="inline-flex items-center gap-1 text-light-2 min-w-0" data-id="element-932">
              <HouseLine size={12} weight="regular" className="text-light-3 shrink-0" data-id="element-933" />
              <span className="truncate max-w-[260px]" title={propertyLabel} data-id="element-934">
                {propertyLabel}
              </span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0" data-id="element-935">
          <button type="button" onClick={onConfirm} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-xs font-semibold transition-colors cursor-pointer" data-id="element-936">
            <Check size={12} weight="bold" data-id="element-937" />
            Confirmă
          </button>
          <button type="button" onClick={onToggle} aria-expanded={expanded} aria-label={expanded ? 'Ascunde detalii' : 'Vezi detalii'} className="inline-flex items-center justify-center w-8 h-8 rounded-md text-light-3 hover:text-light-1 hover:bg-lime-4/40 transition-colors cursor-pointer" data-id="element-938">
            <CaretDown size={14} weight="bold" className={cn('transition-transform', expanded && 'rotate-180')} data-id="element-939" />
          </button>
        </div>
      </div>
      {expanded && <div className="px-5 pb-4 -mt-1 space-y-3 animate-in fade-in slide-in-from-top-1 duration-150" data-id="element-940">
          {entry.offer.message && <div className="rounded-xl bg-dark-2 border border-lime-4/40 p-3" data-id="element-941">
              <p className="text-xs text-light-3 mb-1 uppercase tracking-wider" data-id="element-942">
                Mesajul tău
              </p>
              <p className="text-sm text-light-2 italic leading-relaxed" data-id="element-943">
                "{entry.offer.message}"
              </p>
            </div>}
          <div className="flex flex-wrap items-center gap-4" data-id="element-944">
            <button type="button" onClick={onReport} className="inline-flex items-center gap-1.5 text-sm text-light-3 hover:text-light-1 hover:underline underline-offset-4 cursor-pointer transition-colors" data-id="element-945">
              <Warning size={14} weight="regular" data-id="element-946" />
              Ceva nu e în regulă
            </button>
          </div>
        </div>}
    </li>;
}